import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Header } from '@/components/layout/header';
import { Main } from '@/components/layout/main';
import { TopNav } from '@/components/layout/top-nav';
import { ProfileDropdown } from '@/components/profile-dropdown';
import { Search } from '@/components/search';
import { ThemeSwitch } from '@/components/theme-switch';
import { Button } from '@/components/ui/button';
import { BookOpen, Download } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useEffect, useState } from 'react';
import apiClient from '@/lib/axios';
import { toast } from '@/hooks/use-toast';

const topNav = [
  {
    title: "Ana Sayfa",
    href: "/",
    isActive: false,
  },
  {
    title: "Bölümler",
    href: "/bolumler",
    isActive: false,
  },
  {
    title: "Eğitmenler",
    href: "/egitmenler",
    isActive: false,
  },
];

interface DersNotu {
  _id: string;
  baslik: string;
  konu: string;
  tarih: string;
  url: string;
  dersId?: {
    _id: string;
    ad: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

interface Ders {
  _id: string;
  ad: string;
}

interface Bolum {
  _id: string;
  ad: string;
}

export const Route = createFileRoute('/_authenticated/bolumler/bolumDetay/$bolumId/dersNotlari/$dersId')({
  component: DersNotlariPage,
});

function DersNotlariPage() {
  const { bolumId, dersId } = Route.useParams();
  const [dersNotlari, setDersNotlari] = useState<DersNotu[]>([]);
  const [ders, setDers] = useState<Ders | null>(null);
  const [bolum, setBolum] = useState<Bolum | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dersNotlariResponse, dersResponse, bolumResponse] = await Promise.all([
          apiClient.get(`/ders-notu/ders/${dersId}`),
          apiClient.get(`/ders/${dersId}`),
          apiClient.get(`/bolum/${bolumId}`),
        ]);

        // Endpoint.md'ye göre: GET /ders-notu/ders/:_id - Belirli derse ait notları listele
        // Response array olarak gelir ve bölüm/eğitmen bilgileri populate edilir
        const notlar = Array.isArray(dersNotlariResponse.data) 
          ? dersNotlariResponse.data 
          : [];

        setDersNotlari(notlar);
        setDers(dersResponse.data);
        setBolum(bolumResponse.data);
      } catch (err) {
        setError('Ders notları yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [bolumId, dersId]);

  if (loading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center space-x-4">
            <Search />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="space-y-6">
            <div>
              <Skeleton className="h-8 w-64 mb-2" />
              <Skeleton className="h-4 w-48" />
            </div>
            <Separator />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(4)].map((_, i) => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-6 w-3/4" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <Skeleton className="h-4 w-full" />
                      <div className="flex gap-4">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Skeleton className="h-9 w-32" />
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center space-x-4">
            <Search />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="text-center py-8 text-red-500">{error}</div>
        </Main>
      </>
    );
  }

  if (!bolum || !ders) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center space-x-4">
            <Search />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Ders Bulunamadı!</h1>
          <p className="text-gray-600">Aradığınız ders mevcut değil veya silinmiş olabilir.</p>
        </div>
      </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center space-x-4">
          <Search />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold">{ders.ad}</h1>
            <p className="text-muted-foreground">{bolum.ad}</p>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {dersNotlari.length > 0 ? (
              dersNotlari.map((not) => {
                const formatTarih = () => {
                  if (!not.tarih) return '';
                  try {
                    return new Date(not.tarih).toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    });
                  } catch {
                    return not.tarih;
                  }
                };

                return (
                  <Card key={not._id}>
                  <CardHeader>
                      <CardTitle className="text-lg font-semibold break-words">{not.baslik}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col space-y-4">
                        {not.konu && (
                      <div className="flex items-center gap-2">
                            <span className="text-muted-foreground break-words">{not.konu}</span>
                      </div>
                        )}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
                            {formatTarih() && <span>Tarih: {formatTarih()}</span>}
                            {not.dersId?.ad && <span>Ders: {not.dersId.ad}</span>}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                            className="gap-2 w-full sm:w-auto"
                            onClick={async () => {
                              if (not.url) {
                                let toastResult: { update: (props: any) => void; dismiss: () => void } | null = null;
                                
                                try {
                                  // Progress gösteren toast başlat ve update fonksiyonunu al
                                  toastResult = toast({
                                    title: 'PDF İndiriliyor',
                                    description: `${not.baslik} indiriliyor... 0%`,
                                  });

                                  // PDF'i progress ile indir
                                  const response = await fetch(not.url);
                                  
                                  if (!response.ok) {
                                    throw new Error('Network response was not ok');
                                  }

                                  const contentLength = response.headers.get('content-length');
                                  const total = contentLength ? parseInt(contentLength, 10) : 0;
                                  
                                  if (!response.body) {
                                    throw new Error('Response body is null');
                                  }

                                  const reader = response.body.getReader();
                                  const chunks: BlobPart[] = [];
                                  let receivedLength = 0;
                                  let lastProgressUpdate = 0;

                                  // Stream'i oku
                                  while (true) {
                                    const { done, value } = await reader.read();
                                    if (done) break;
                                    
                                    chunks.push(value);
                                    receivedLength += value.length;
                                    
                                    // Her %5'te bir progress güncelle (spam'i önlemek için)
                                    const progress = total > 0 ? Math.round((receivedLength / total) * 100) : 0;
                                    if (progress - lastProgressUpdate >= 5 || progress === 100) {
                                      lastProgressUpdate = progress;
                                      const downloadedMB = (receivedLength / (1024 * 1024)).toFixed(2);
                                      const totalMB = total > 0 ? (total / (1024 * 1024)).toFixed(2) : '?';
                                      
                                      // Mevcut toast'u güncelle (yeni toast açma)
                                      if (toastResult?.update) {
                                        toastResult.update({
                                          title: 'PDF İndiriliyor',
                                          description: `${not.baslik} - %${progress} (${downloadedMB} MB / ${totalMB} MB)`,
                                        });
                                      }
                                    }
                                  }

                                  // Toast'u kapat
                                  if (toastResult?.dismiss) {
                                    toastResult.dismiss();
                                  }

                                  // Blob oluştur
                                  const blob = new Blob(chunks);
                                  const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                                  link.href = url;
                                  const dosyaAdi = `${not.baslik}.pdf` || 'ders-notu.pdf';
                                  link.download = dosyaAdi;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                  
                                  // Başarılı toast göster
                                  toast({
                                    title: 'PDF İndirildi',
                                    description: `${not.baslik} başarıyla indirildi`,
                                  });
                                } catch (error) {
                                  // Progress toast'unu kapat
                                  if (toastResult?.dismiss) {
                                    toastResult.dismiss();
                                  }
                                  
                                  // Hata toast göster
                                  toast({
                                    variant: 'destructive',
                                    title: 'İndirme Hatası',
                                    description: 'PDF indirilirken bir hata oluştu',
                                  });
                                  
                                  // Hata durumunda fallback olarak yeni sekmede aç
                                  window.open(not.url, '_blank');
                                }
                              }
                          }}
                        >
                          <Download className="h-4 w-4" />
                            PDF İndir
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })
            ) : (
              <Card className="col-span-full">
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground text-center">Bu ders için henüz not yüklenmemiş.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </Main>
    </>
  );
} 
